import { ListResponse } from '@/types/common';
import {
  BuildTargetListResponse,
  SourceCompanyResponse,
  SourceContactResponse,
  TargetList,
  TargetListImportResponse,
} from '@/types/targetList';
import { TargetListContactsResponse, TargetListImportContact } from '@/types/targetListContact';
import fetchWithAuth from './base';
import { TargetListImportCompany } from '@/types/targetListCompany';
import { SourceCompanyConfig } from '@/types/workflow';
import { withSearchFilter, type FilterTree } from '@/app/components/query-builder/grouped-query';

export type TargetListFilter = { field: string; operator: string; value: unknown };

export const getTargetLists = async ({
  clientId,
  startingAfter,
  endingBefore,
  limit,
  search,
  status,
  filters,
  campaignId,
  companyId,
  contactId,
}: {
  clientId: string;
  startingAfter?: string;
  endingBefore?: string;
  limit?: number;
  search?: string;
  status?: string;
  filters?: TargetListFilter[];
  campaignId?: string;
  companyId?: string;
  contactId?: string;
}): Promise<ListResponse<TargetList>> => {
  const requestFilters = [
    ...(filters || []),
    ...(status ? [{ field: 'status', operator: 'eq', value: status }] : []),
  ];

  if (requestFilters.length) {
    const response = await fetchWithAuth(`/api/client/${clientId}/target_list/search`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        filter: { match: 'all', rules: requestFilters },
        ...(startingAfter ? { starting_after: startingAfter } : {}),
        ...(endingBefore ? { ending_before: endingBefore } : {}),
        ...(limit ? { limit } : {}),
        ...(search ? { search } : {}),
        ...(campaignId ? { campaign_id: campaignId } : {}),
        ...(companyId ? { company_id: companyId } : {}),
        ...(contactId ? { contact_id: contactId } : {}),
      }),
    });
    return response.json();
  }

  const params = new URLSearchParams();

  if (startingAfter) {
    params.append('starting_after', startingAfter);
  }

  if (endingBefore) {
    params.append('ending_before', endingBefore);
  }

  if (limit) {
    params.append('limit', limit.toString());
  }

  if (search) {
    params.append('search', search);
  }

  if (campaignId) {
    params.append('campaign_id', campaignId);
  }

  if (companyId) {
    params.append('company_id', companyId);
  }

  const response = await fetchWithAuth(`/api/client/${clientId}/target_list?${params.toString()}`, {
    method: 'GET',
  });
  return response.json();
};

export const getContactTargetLists = async ({
  clientId,
  contactId,
  limit,
}: {
  clientId: string;
  contactId: string;
  limit?: number;
}): Promise<ListResponse<TargetList>> => {
  const params = new URLSearchParams();
  if (limit) params.set('limit', limit.toString());

  const response = await fetchWithAuth(
    `/api/client/${clientId}/contact/${contactId}/target_list${params.size ? `?${params.toString()}` : ''}`,
    { method: 'GET' }
  );
  const data: Array<TargetList & { overall_contacts_count?: number }> = await response.json();
  const normalizedData = data.map(targetList => ({
    ...targetList,
    contact_count: targetList.overall_contacts_count ?? targetList.contact_count ?? 0,
  }));

  return {
    data: normalizedData,
    total: normalizedData.length,
    starting_after: '',
    ending_before: '',
    has_more: false,
  };
};

export const getTargetListCount = async (
  clientId: string,
  filters?: TargetListFilter[]
): Promise<{
  total: number;
  running?: number;
  not_running?: number;
  draft: number;
  error?: number;
  deleted?: number;
}> => {
  const response = await fetchWithAuth(`/api/client/${clientId}/target_list/count`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(filters?.length ? { filter: { match: 'any', rules: filters } } : {}),
  });
  return response.json();
};

export const createTargetList = async (id: string, data: Partial<TargetList>) => {
  const response = await fetchWithAuth(`/api/client/${id}/target_list`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.json();
};

export const updateTargetList = async (
  id: string,
  list_id: string,
  data: Partial<TargetList>
): Promise<TargetList | null> => {
  const response = await fetchWithAuth(`/api/client/${id}/target_list/${list_id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
  if (response.status === 204) {
    return null;
  }
  return response.json();
};

export const getTargetListById = async (id: string, list_id: string) => {
  const response = await fetchWithAuth(`/api/client/${id}/target_list/${list_id}`, {
    method: 'GET',
  });
  return response.json();
};

export const importTargetList = async (
  id: string,
  list_id: string,
  file: File
): Promise<TargetListImportResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  console.log('formData', formData, file);
  const response = await fetchWithAuth(`/api/client/${id}/target_list/${list_id}/import`, {
    method: 'POST',
    body: formData,
  });
  return response.json();
};

export const getImportTargetList = async (
  clientId: string,
  targetListId: string
): Promise<ListResponse<TargetListImportResponse>> => {
  const response = await fetchWithAuth(
    `/api/client/${clientId}/target_list/${targetListId}/import`,
    {
      method: 'GET',
    }
  );
  return response.json();
};

export const getTargetListContacts = async ({
  clientId,
  targetListId,
  startingAfter,
  endingBefore,
  limit = 100,
  search,
  importId,
  persona_ids,
  company_ids,
  titles,
  created_at_start,
  created_at_end,
  has_phone,
  has_email,
  has_linkedin,
  filterSearchParams,
  filters,
  match,
  filter,
}: {
  clientId: string;
  targetListId: string;
  startingAfter?: string;
  endingBefore?: string;
  limit?: number;
  search?: string;
  importId?: string;
  persona_ids?: string[];
  company_ids?: string[];
  titles?: string[];
  created_at_start?: string;
  created_at_end?: string;
  has_phone?: string;
  has_email?: string;
  has_linkedin?: string;
  filterSearchParams?: URLSearchParams;
  filters?: Array<{ field: string; operator: string; value: unknown }>;
  match?: 'all' | 'any';
  targetPages?: Array<{
    conditions: Array<{ field: string; operator: string; value: unknown }>;
  }>;
  filter?: FilterTree;
}): Promise<TargetListContactsResponse> => {
  const params = new URLSearchParams(filterSearchParams || {});

  if (startingAfter) {
    params.append('starting_after', startingAfter);
  }

  if (endingBefore) {
    params.append('ending_before', endingBefore);
  }

  params.set('limit', limit.toString());

  const requestFilters = [
    ...(filters || []),
    ...(search ? [{ field: 'search', operator: 'eq', value: search }] : []),
  ];

  if (importId) {
    params.append('import_id', importId);
  }

  if (persona_ids && persona_ids.length > 0) {
    persona_ids.forEach(id => params.append('persona_ids', id));
  }

  if (company_ids && company_ids.length > 0) {
    company_ids.forEach(id => params.append('company_ids', id));
  }

  if (titles && titles.length > 0) {
    titles.forEach(title => params.append('titles', title));
  }

  if (created_at_start) {
    params.append('created_at_start', created_at_start);
  }

  if (created_at_end) {
    params.append('created_at_end', created_at_end);
  }

  if (has_phone) {
    params.append('has_phone', has_phone);
  }

  if (has_email) {
    params.append('has_email', has_email);
  }

  if (has_linkedin) {
    params.append('has_linkedin', has_linkedin);
  }

  const response =
    filter || requestFilters.length
      ? await fetchWithAuth(`/api/client/${clientId}/target_list/${targetListId}/contact/search`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...(startingAfter ? { starting_after: startingAfter } : {}),
            limit,
            ...(filter
              ? { filter: withSearchFilter(filter, search) }
              : {
                  ...(requestFilters.length
                    ? { filter: { match: match || 'all', rules: requestFilters } }
                    : {}),
                }),
          }),
        })
      : await fetchWithAuth(
          `/api/client/${clientId}/target_list/${targetListId}/contact${params.size ? `?${params.toString()}` : ''}`,
          { method: 'GET' }
        );
  return response.json();
};

export const getTargetList = async (
  clientId: string,
  targetListId: string
): Promise<TargetList> => {
  const response = await fetchWithAuth(`/api/client/${clientId}/target_list/${targetListId}`, {
    method: 'GET',
  });
  return response.json();
};

export const deleteTargetList = async (clientId: string, targetListId: string) => {
  const response = await fetchWithAuth(`/api/client/${clientId}/target_list/${targetListId}`, {
    method: 'DELETE',
  });

  if (response.status === 204) {
    return { status: 204 };
  }

  // For other responses, try to parse JSON
  try {
    const data = await response.json();
    return { ...data, status: response.status };
  } catch {
    // If JSON parsing fails, return the response with status
    return { status: response.status, error: 'Failed to parse response' };
  }
};

export const restoreTargetList = async (clientId: string, targetListId: string) => {
  const response = await fetchWithAuth(
    `/api/client/${clientId}/target_list/${targetListId}/restore`,
    {
      method: 'POST',
    }
  );
  return response.json();
};

export const deleteImportTargetList = async (
  clientId: string,
  targetListId: string,
  importId: string
) => {
  const response = await fetchWithAuth(
    `/api/client/${clientId}/target_list/${targetListId}/import/${importId}`,
    {
      method: 'DELETE',
    }
  );
  return response.json();
};

export const getTargetListImportCompanies = async ({
  clientId,
  targetListId,
  startingAfter,
  endingBefore,
  created_at_start,
  created_at_end,
  limit,
  search,
  importId,
}: {
  clientId: string;
  targetListId: string;
  startingAfter?: string;
  endingBefore?: string;
  created_at_start?: string;
  created_at_end?: string;
  limit?: number;
  search?: string;
  importId?: string;
}): Promise<ListResponse<TargetListImportCompany> & { preview_status: string }> => {
  const params = new URLSearchParams();

  if (startingAfter) {
    params.append('starting_after', startingAfter);
  }

  if (endingBefore) {
    params.append('ending_before', endingBefore);
  }

  if (limit) {
    params.append('limit', limit.toString());
  }

  if (search) {
    params.append('search', search);
  }

  if (importId) {
    params.append('import_id', importId);
  }

  if (created_at_start) {
    params.append('created_at_start', created_at_start);
  }

  if (created_at_end) {
    params.append('created_at_end', created_at_end);
  }

  const response = await fetchWithAuth(
    `/api/client/${clientId}/target_list/${targetListId}/import/${importId}/company?${params.toString()}`,
    {
      method: 'GET',
    }
  );
  return response.json();
};

export const getTargetListImportContacts = async ({
  clientId,
  targetListId,
  startingAfter,
  endingBefore,
  limit,
  search,
  importId,
}: {
  clientId: string;
  targetListId: string;
  startingAfter?: string;
  endingBefore?: string;
  limit?: number;
  search?: string;
  importId?: string;
}): Promise<ListResponse<TargetListImportContact> & { preview_status: string }> => {
  const params = new URLSearchParams();

  if (startingAfter) {
    params.append('starting_after', startingAfter);
  }

  if (endingBefore) {
    params.append('ending_before', endingBefore);
  }

  if (limit) {
    params.append('limit', limit.toString());
  }

  if (search) {
    params.append('search', search);
  }

  if (importId) {
    params.append('import_id', importId);
  }

  const response = await fetchWithAuth(
    `/api/client/${clientId}/target_list/${targetListId}/import/${importId}/contact?${params.toString()}`,
    {
      method: 'GET',
    }
  );
  return response.json();
};
export const updateImportTargetListCompany = async (
  clientId: string,
  targetListId: string,
  importId: string,
  data: { company_ids: string[] }
) => {
  const response = await fetchWithAuth(
    `/api/client/${clientId}/target_list/${targetListId}/import/${importId}/company`,
    {
      method: 'PATCH',
      body: JSON.stringify(data),
    }
  );
  if (response.status === 204) {
    return null;
  }
  return response.json();
};

export const updateSourceTargetListCompany = async (
  clientId: string,
  targetListId: string,
  importId: string,
  data: { company_ids: string[] }
) => {
  const response = await fetchWithAuth(
    `/api/client/${clientId}/target_list/${targetListId}/source/${importId}/company`,
    {
      method: 'PATCH',
      body: JSON.stringify(data),
    }
  );
  if (response.status === 204) {
    return null;
  }
  return response.json();
};

export const updateImportTargetListContact = async (
  clientId: string,
  targetListId: string,
  importId: string,
  data: { contact_ids: string[] }
) => {
  const response = await fetchWithAuth(
    `/api/client/${clientId}/target_list/${targetListId}/import/${importId}/contact`,
    {
      method: 'PATCH',
      body: JSON.stringify(data),
    }
  );
  if (response.status === 204) {
    return null;
  }
  return response.json();
};

export const buildTargetList = async (
  clientId: string,
  targetListId: string
): Promise<BuildTargetListResponse | null> => {
  const response = await fetchWithAuth(
    `/api/client/${clientId}/target_list/${targetListId}/build`,
    {
      method: 'POST',
    }
  );
  if (response.status === 204) {
    return null;
  }

  return response.json();
};

export const archiveTargetList = async (clientId: string, targetListId: string) => {
  const response = await fetchWithAuth(`/api/client/${clientId}/target_list/${targetListId}`, {
    method: 'DELETE',
  });
  if (response.status === 204) {
    return { status: 204 };
  }
  try {
    const data = await response.json();
    return { ...data, status: response.status };
  } catch {
    return { status: response.status, error: 'Failed to parse response' };
  }
};

export const deleteTargetListContact = async (
  clientId: string,
  targetListId: string,
  contactIds: string[]
) => {
  const response = await fetchWithAuth(
    `/api/client/${clientId}/target_list/${targetListId}/contact`,
    {
      method: 'DELETE',
      body: JSON.stringify({ contact_ids: contactIds }),
    }
  );
  if (response.status === 204) {
    return { status: 204 };
  }
  try {
    const data = await response.json();
    return { ...data, status: response.status };
  } catch {
    return { status: response.status, error: 'Failed to parse response' };
  }
};

export const addContactsToTargetList = async (
  clientId: string,
  targetListId: string,
  contactIds: string[]
) => {
  await fetchWithAuth(`/api/client/${clientId}/target_list/${targetListId}/contact`, {
    method: 'POST',
    body: JSON.stringify({ contact_ids: contactIds }),
  });
};

export const addCompaniesToTargetList = async (
  clientId: string,
  targetListId: string,
  companyIds: string[],
  personaIds: string[],
  filterSearchParams?: URLSearchParams
) => {
  const params = new URLSearchParams(filterSearchParams);

  const query = params?.size ? `?${params.toString()}` : '';
  await fetchWithAuth(`/api/client/${clientId}/target_list/${targetListId}/company${query}`, {
    method: 'POST',
    body: JSON.stringify({
      ...(companyIds.length ? { company_ids: companyIds } : {}),
      ...(personaIds.length ? { persona_ids: personaIds } : {}),
    }),
  });
};

export const sourceContact = async (
  clientId: string,
  listId: string,
  workflowId: string,
  data: {
    persona_ids: string[];
    locations: string[];
    node_id: string;
    node_type: string;
    contacts_limit?: number;
    per_persona_limit?: number;
    company_website_visitor_id?: string;
    exclude_companies_with_contacts?: boolean;
  }
): Promise<SourceContactResponse> => {
  const response = await fetchWithAuth(
    `/api/client/${clientId}/target_list/${listId}/workflow/${workflowId}/source/contact`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  );
  return response.json();
};

export const sourceCompany = async (
  clientId: string,
  listId: string,
  workflowId: string,
  data: SourceCompanyConfig
): Promise<SourceCompanyResponse> => {
  const response = await fetchWithAuth(
    `/api/client/${clientId}/target_list/${listId}/workflow/${workflowId}/source/company`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  );
  return response.json();
};

export const getVisitorSourceContact = async (
  clientId: string,
  listId: string,
  visitorId: string
) => {
  const response = await fetchWithAuth(
    `/api/client/${clientId}/target_list/${listId}/source/${visitorId}/contact`,
    {
      method: 'GET',
    }
  );
  return response.json();
};

export const updateSourceTargetListContact = async (
  clientId: string,
  listId: string,
  visitorId: string,
  data: {
    contact_ids: string[];
  }
) => {
  const response = await fetchWithAuth(
    `/api/client/${clientId}/target_list/${listId}/source/${visitorId}/contact`,
    {
      method: 'PATCH',
      body: JSON.stringify(data),
    }
  );
  if (response.status === 204) {
    return null;
  }
  return response.json();
};
